import React from 'react';
import { Text, TouchableOpacity, View, StyleSheet, Platform } from 'react-native';
import dayjs from 'dayjs';
import { Ionicons, MaterialIcons } from '@expo/vector-icons';
import _ from 'lodash';
import { useMyBarTimeTableStore } from 'src/domain/stores/useMyBarTimeTableStore';
import Fonts from 'src/core/utils/Fonts';
import Dimens from 'src/core/utils/Dimens';
import Colors from 'src/core/utils/Colors';
import Constants from 'src/core/utils/Constants';
import type { TimeTablePageItem, TimeTablePageScreenParams } from 'src/presentation/navigation/types/NavigationTypes';

interface FavoTimeTableItemProps {
	data: { item: TimeTablePageItem };
	noBorderBottom?: boolean;
	flag?: boolean;
	onNavigateToTimetablePage: (params: TimeTablePageScreenParams) => void;
}

function FavoTimeTableItem({ data, noBorderBottom, flag, onNavigateToTimetablePage }: FavoTimeTableItemProps) {
	// Straight from the store, which owns how a session is identified: rebuilding that key here left the heart
	// empty on a session the store did hold.
	const favorite = useMyBarTimeTableStore((state) => state.isFavorite(data.item));
	const toggleFavorite = useMyBarTimeTableStore((state) => state.toggleFavorite);

	// null when the backend could not read the date; better than an epoch time.
	const getTimeFromTimestamp = (time: number | null) => {
		return time === null ? '' : dayjs.unix(time).format(Constants.timeFormat);
	};

	const onToggleFavorite = () => {
		toggleFavorite(data.item, data.item.eventUuid);
	};

	const showItem = (item: TimeTablePageItem) => {
		onNavigateToTimetablePage({
			data: item,
			title: item.navTitle,
		});
	};

	const { item } = data;

	return (
		<View style={noBorderBottom ? [styles.noLine, styles.listItem] : styles.listItem}>
			<TouchableOpacity
				style={styles.itemContainer}
				onPress={_.debounce(() => showItem(item), 800, {
					leading: true,
					trailing: false,
				})}
			>
				<View style={styles.body}>
					<Text style={styles.heading}>{item.title}</Text>
					<Text
						style={
							!flag ? styles.description : [styles.description, { color: Colors.red, fontFamily: Fonts.montserratMedium }]
						}
					>
						{getTimeFromTimestamp(item.startdate)} - {getTimeFromTimestamp(item.enddate)}
					</Text>
				</View>
				<TouchableOpacity
					accessibilityRole="button"
					accessibilityState={{ selected: favorite }}
					style={styles.favoriteButton}
					testID="favorite-toggle"
					onPress={onToggleFavorite}
				>
					{Platform.OS === 'ios' ? (
						<Ionicons
							name={favorite ? 'heart' : 'heart-outline'}
							size={26}
							style={{ color: Colors.red }}
							testID="favorite-icon"
						/>
					) : (
						<MaterialIcons
							name="favorite"
							size={24}
							style={favorite ? { color: Colors.red } : { color: Colors.overlay12 }}
							testID="favorite-icon"
						/>
					)}
				</TouchableOpacity>
			</TouchableOpacity>
		</View>
	);
}

export default FavoTimeTableItem;

const styles = StyleSheet.create({
	noLine: {
		borderBottomWidth: Platform.OS === 'ios' ? 0 : undefined,
	},
	listItem: {
		paddingTop: Platform.OS === 'ios' ? Dimens.padding0 : Dimens.padding2,
		paddingBottom: Platform.OS === 'ios' ? Dimens.padding0 : Dimens.padding2,
		marginLeft: Platform.OS === 'ios' ? Dimens.margin20 : undefined,
		paddingRight: Platform.OS === 'ios' ? Dimens.padding5 : undefined,
	},
	itemContainer: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		paddingVertical: Dimens.padding12,
	},
	body: {
		flex: 1,
	},
	heading: {
		fontSize: Platform.OS === 'ios' ? Dimens.fontSize17 : Dimens.fontSize16,
		color: Colors.blackPure,
		fontFamily: Fonts.montserratRegular,
		paddingBottom: Dimens.padding5,
	},
	description: {
		color: Platform.OS === 'ios' ? Colors.greyTextDark : Colors.overlay54,
		fontSize: Platform.OS === 'ios' ? Dimens.fontSize12 : Dimens.fontSize14,
		fontFamily: Fonts.montserratRegular,
	},
	favoriteButton: {
		width: 54,
		height: 54,
		alignItems: 'center',
		justifyContent: 'center',
	},
});
